/* hs-eslint ignored failing-rules */
/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable promise/catch-or-return */

'use es6';

import quickFetch from 'quick-fetch';
import Raven from 'raven-js';
import { List, fromJS } from 'immutable';
import debounce from 'transmute/debounce';
import identity from 'transmute/identity';
import { createAction } from 'flux-actions';
import SearchDebounceDelay from 'SalesContentIndexUI/data/constants/SearchDebounceDelay';
import { SET_SEARCH_QUERY, SET_SORT, SET_SELECTED_FOLDER, SET_VIEW_FILTER, SEARCH_FETCH_SUCCEEDED, FOLDER_FETCH_SUCCEEDED, SEARCH_FETCH_FAILED, SET_FROM_QUERY_PARAMS, SET_FROM_MODAL } from 'SalesContentIndexUI/data/constants/ActionTypes';
import SearchStatus from '../constants/SearchStatus';
import SearchQueryRecord from '../records/SearchQueryRecord';
import filter from '../utils/filter';
import { CONTENT_ID_FIELD, CONTENT_TYPE_FIELD } from '../constants/SearchFields';
import { UserViewFilter } from 'SalesContentIndexUI/data/utils/viewFilters/UserViewFilter';
import { TeamViewFilter } from 'SalesContentIndexUI/data/utils/viewFilters/TeamViewFilter';
import * as FilterTypes from 'SalesContentIndexUI/data/lib/FilterTypes';
const setSearchQueryAction = createAction(SET_SEARCH_QUERY, identity);
const searchFetchSucceededAction = createAction(SEARCH_FETCH_SUCCEEDED, identity);
const folderFetchSucceededAction = createAction(FOLDER_FETCH_SUCCEEDED, identity);
const searchFetchFailedAction = createAction(SEARCH_FETCH_FAILED, identity);
const setSortAction = createAction(SET_SORT, identity);
const setSelectedFolderAction = createAction(SET_SELECTED_FOLDER, identity);
const setViewFilterAction = createAction(SET_VIEW_FILTER, identity);
const setFromQueryParamsAction = createAction(SET_FROM_QUERY_PARAMS, identity);
const setFromModalAction = createAction(SET_FROM_MODAL, identity);
const getSearchQuery = getState => getState().search.searchQuery;
const getSelectedViewFilter = getState => getState().search.selectedViewFilter;
const getMostRecentSort = getState => getState().search.mostRecentSort;
const getSearchStatus = state => state.search.searchStatus;
const fetchWithEarlyRequestHook = (fallbackFetch, requestName) => {
  const earlyRequest = quickFetch.getRequestStateByName(requestName);
  if (earlyRequest) {
    return new Promise((resolve, reject) => {
      earlyRequest.whenFinished(result => {
        resolve(fromJS(result));
      });
      earlyRequest.onError(xhrError => {
        Raven.captureMessage('early request failed', {
          extra: {
            responseText: xhrError.responseText,
            status: xhrError.status
          }
        });
        return fallbackFetch().then((...args) => resolve(...args), (...args) => reject(...args));
      });
    });
  }
  return fallbackFetch();
};
const getActions = ({
  searchFetch,
  looseItemContentType,
  folderContentType,
  defaultViewFilter,
  searchOptions
}) => {
  const {
    onSetSort = identity,
    onSetViewFilter = identity,
    onSetFolder = identity,
    onSearch = identity
  } = searchOptions;
  const search = ({
    dispatch,
    searchQuery,
    selectedViewFilter,
    isInitial
  }) => {
    const searchQueryOverride = selectedViewFilter && selectedViewFilter.getSearchQueryOverride && selectedViewFilter.getSearchQueryOverride({
      searchQuery
    });
    const selectedSearchQuery = searchQueryOverride || searchQuery;
    const fallbackFetch = () => searchFetch(selectedSearchQuery);
    const searchPromise = isInitial ? fetchWithEarlyRequestHook(fallbackFetch, 'index-search') : fallbackFetch();
    return searchPromise.then(results => dispatch(searchFetchSucceededAction(results)), err => dispatch(searchFetchFailedAction(err)));
  };
  const debouncedSearchFetch = debounce(SearchDebounceDelay, search);
  const fetchSelectedFolder = searchQuery => dispatch => {
    const folderId = searchQuery.getFolderFilter().values.first();
    if (!folderId) {
      return;
    }
    const folderSearchQuery = new SearchQueryRecord({
      limit: 1,
      filters: List([filter(CONTENT_ID_FIELD, folderId), filter(CONTENT_TYPE_FIELD, folderContentType)])
    });
    searchFetch(folderSearchQuery).then(res => {
      dispatch(folderFetchSucceededAction(res.get('results').first()));
    });
  };
  return {
    searchFromModal: ({
      isInitial = false
    }) => (dispatch, getState) => {
      const searchQuery = getSearchQuery(getState);
      const selectedViewFilter = getSelectedViewFilter(getState);
      dispatch(setFromModalAction({
        isInitial,
        searchQuery
      }));
      search({
        dispatch,
        searchQuery,
        selectedViewFilter
      });
    },
    searchFromQueryParams: ({
      queryParams,
      isInitial = false,
      selectedSort = null
    }) => (dispatch, getState) => {
      const {
        view,
        type
      } = queryParams;
      const searchQuery = getSearchQuery(getState);
      let selectedFilter;
      if (!type) {
        selectedFilter = getState().viewFilters.get(view);
      } else {
        const ViewFilter = type === FilterTypes.USER ? UserViewFilter : TeamViewFilter;
        selectedFilter = ViewFilter({
          looseItemContentType,
          folderContentType,
          id: view
        });
      }
      const selectedViewFilter = selectedFilter || defaultViewFilter;
      const updatedSearchQuery = searchQuery.mergeWithQueryParams({
        queryParams,
        selectedViewFilter
      }).update('sorts', sorts => selectedSort === null ? sorts : List.of(selectedSort));
      if (isInitial && getSearchStatus(getState()) === SearchStatus.SUCCEEDED) {
        return null;
      }
      dispatch(setFromQueryParamsAction({
        isInitial,
        selectedViewFilter,
        searchQuery: updatedSearchQuery
      }));
      if (updatedSearchQuery.getFolderFilter()) {
        dispatch(fetchSelectedFolder(updatedSearchQuery));
      }
      return search({
        dispatch,
        selectedViewFilter,
        searchQuery: updatedSearchQuery,
        isInitial
      });
    },
    setSearchQuery: query => (dispatch, getState) => {
      const searchQuery = getSearchQuery(getState);
      const selectedViewFilter = getSelectedViewFilter(getState);
      const noSorts = List();
      const sorts = query ? noSorts : List([getMostRecentSort(getState)]);
      const updatedSearchQuery = searchQuery.merge({
        query,
        sorts,
        offset: 0
      });
      onSearch(looseItemContentType);
      dispatch(setSearchQueryAction(updatedSearchQuery));
      debouncedSearchFetch({
        dispatch,
        selectedViewFilter,
        searchQuery: updatedSearchQuery
      });
    },
    getPage: page => (dispatch, getState) => {
      const searchQuery = getSearchQuery(getState);
      const selectedViewFilter = getSelectedViewFilter(getState);
      const {
        limit
      } = searchQuery;
      const newOffset = page * limit;
      const updatedSearchQuery = searchQuery.set('offset', newOffset);
      dispatch(setSearchQueryAction(updatedSearchQuery));
      return search({
        dispatch,
        selectedViewFilter,
        searchQuery: updatedSearchQuery
      });
    },
    setSort: ({
      selectedSort,
      resetSearch = false
    }) => (dispatch, getState) => {
      const searchQuery = getSearchQuery(getState);
      const selectedViewFilter = getSelectedViewFilter(getState);
      const updatedSearchQuery = searchQuery.merge({
        query: resetSearch ? '' : searchQuery.query,
        offset: 0,
        sorts: List([selectedSort])
      });
      dispatch(setSortAction({
        selectedSort,
        searchQuery: updatedSearchQuery
      }));
      onSetSort(looseItemContentType, selectedSort);
      return search({
        dispatch,
        selectedViewFilter,
        searchQuery: updatedSearchQuery
      });
    },
    setSelectedFolder: folderSearchResult => (dispatch, getState) => {
      const searchQuery = getSearchQuery(getState);
      const selectedViewFilter = getSelectedViewFilter(getState);
      const folderId = folderSearchResult && folderSearchResult.contentId;
      const updatedSearchQuery = searchQuery.setFolderFilter(folderId).set('offset', 0);
      dispatch(setSelectedFolderAction({
        selectedFolder: folderSearchResult,
        searchQuery: updatedSearchQuery
      }));
      onSetFolder(looseItemContentType, folderId);
      return search({
        dispatch,
        selectedViewFilter,
        searchQuery: updatedSearchQuery
      });
    },
    setViewFilter: ({
      type,
      id
    }) => (dispatch, getState) => {
      const searchQuery = getSearchQuery(getState);
      let selectedViewFilter;
      if (type === FilterTypes.CUSTOM) {
        selectedViewFilter = getState().viewFilters.get(id);
      } else {
        const ViewFilter = type === FilterTypes.USER ? UserViewFilter : TeamViewFilter;
        selectedViewFilter = ViewFilter({
          looseItemContentType,
          folderContentType,
          id
        });
      }
      const updatedSearchQuery = searchQuery.merge({
        query: '',
        offset: 0,
        filters: selectedViewFilter.getFilters(id),
        sorts: List([selectedViewFilter.getSort()])
      });
      dispatch(setViewFilterAction({
        selectedViewFilter,
        searchQuery: updatedSearchQuery
      }));
      onSetViewFilter(looseItemContentType, selectedViewFilter);
      return search({
        dispatch,
        selectedViewFilter,
        searchQuery: updatedSearchQuery
      });
    }
  };
};
const SearchActions = {
  create() {
    let _actions;
    return {
      init(opts) {
        _actions = getActions(opts);
      },
      get(action) {
        if (!_actions) {
          throw new Error('Must call init on SearchActions before retrieving actions');
        }
        return action ? _actions[action] : _actions;
      }
    };
  }
};
export default SearchActions.create();